export type RevealAnimationTimeline = {
  activationPeakMs: number;
  buildStartMs: number;
  impactPeakMs: number;
  revealOnsetMs: number;
  totalDurationMs: number;
};

export const TAP_THREE_PACKET_COMPRESS_MS = 320;
export const REVEAL_DROP_FADE_MS = 220;
export const BURST_OPENING_SETTLE_AFTER_IMPACT_MS = REVEAL_DROP_FADE_MS + 420;

export const LANDING_REVEAL_TIMELINE = {
  activationPeakMs: 305,
  buildStartMs: 1265,
  impactPeakMs: 2360,
  revealOnsetMs: 2400,
  totalDurationMs: 4829,
} satisfies RevealAnimationTimeline;

export const PACKET_OPENING_STATE_DURATION_MS = 2350;
export const PACKET_ACTION_SCALE = 1.065;
export const PACKET_PRESS_SCALE = 0.955;
export const PACKET_PRESS_SCALE_RESPONSE = 18;
export const PACKET_SETTLE_START_PROGRESS = 0.72;
export const PACKET_SPIN_DURATION_SECONDS = 1.58;
export const PACKET_SPIN_EXPOSURE_AMOUNT = 0.28;
export const PACKET_SPIN_EXPOSURE_RESPONSE = 6.5;
export const PACKET_SPIN_ROTATIONS = 3;
export const PACKET_VISUAL_DELTA_MAX_SECONDS = 1 / 30;
export const PACKET_ENTRANCE_ROTATION_Z = -0.08;
export const PACKET_ENTRANCE_Y = 0;
export const PACKET_SCREEN_Y_OFFSET = 0.36;

export const BURST_ACCELERATION_EXPONENT = 2.25;
export const BURST_COMPRESS_SCALE = 0.74;
export const BURST_FILL_SCREEN_SCALE = 15.4;
export const BURST_LATE_ACCELERATION_EXPONENT = 1.85;
export const BURST_LATE_ACCELERATION_START_PROGRESS = 0.68;
export const BURST_SCALE_BASE_AMOUNT = 4.4;

export function getBurstOpeningStateDurationMs(timeline: RevealAnimationTimeline) {
  return timeline.impactPeakMs + BURST_OPENING_SETTLE_AFTER_IMPACT_MS;
}

export function getPacketScaleDurationMs(timeline: RevealAnimationTimeline) {
  return Math.max(timeline.revealOnsetMs - timeline.buildStartMs, 1);
}

export function getIdlePacketPose(elapsedTime: number) {
  return {
    positionY: Math.sin(elapsedTime * 1.35) * 0.055,
    rotationX: Math.sin(elapsedTime * 0.52) * 0.055,
    rotationY: Math.sin(elapsedTime * 0.82) * 0.24,
  };
}

export function easeOutQuart(value: number) {
  return 1 - (1 - value) ** 4;
}

export function easeOutCubic(value: number) {
  return 1 - (1 - value) ** 3;
}

export function getIdleHeartbeatProgress(elapsedTime: number) {
  const cycleTime = elapsedTime % 5;
  const pulseTime = cycleTime - 0.08;
  const riseDuration = 0.22;
  const holdDuration = 0.35;
  const fallDuration = 0.36;

  if (pulseTime < 0 || pulseTime > riseDuration + holdDuration + fallDuration) {
    return 0;
  }

  if (pulseTime < riseDuration) {
    return easeOutQuart(pulseTime / riseDuration);
  }

  if (pulseTime < riseDuration + holdDuration) {
    return 1;
  }

  return 1 - easeOutCubic((pulseTime - riseDuration - holdDuration) / fallDuration);
}

export function getIdleHeartbeatScale(elapsedTime: number) {
  return 1 + getIdleHeartbeatProgress(elapsedTime) * 0.045;
}

export function smoothstep(value: number, minimum: number, maximum: number) {
  if (minimum === maximum) {
    return value < minimum ? 0 : 1;
  }

  const clamped = Math.min(Math.max((value - minimum) / (maximum - minimum), 0), 1);

  return clamped * clamped * (3 - 2 * clamped);
}
